import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import {
  BUY, DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, DIRECTIONS, SELL, UNTRADED_ENERGY_KEY,
} from 'src/util/constants';

import StackedBarChart from './StackedBarChart';
import {
  TRADE, UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from './chartConstants';
import { compareByX } from '../helpers/chart';
import { getUnitProps, showNoDataMsg } from '../helpers/common';

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - ChartTrade component
 */
function ChartTrade({
  aggregation, chartView, handleChartClick, hoverKeys = [], multipleMeter = false,
  selectedKeys = [], timespan, timezone, tooltipTimestamp = null, tooltipUpdate,
  tooltipLabelFunc, tradeDataBuy = [], tradeDataSell = [], unit, width,
}) {
  const dataNotAvailable = (!tradeDataBuy || tradeDataBuy.length === 0)
    && (!tradeDataSell || tradeDataSell.length === 0);

  if (dataNotAvailable) {
    return showNoDataMsg(TRADE);
  }

  // Logic to frame the chart data : Start
  const {
    key: unitKey, label: unitLabel, format: yAxisFormat, tooltipFormat,
  } = getUnitProps(unit);
  const tooltipDateFormat = aggregation.match(/^PT\d+[HMS]/) ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT;

  const { data, tooltipData } = useMemo(() => {
    const processedData = { buy: {}, sell: {} };
    const offset = {};
    const allXAxisValues = {};
    const processedTooltipData = {
      buy: {},
      sell: {},
      all: {},
    };

    DIRECTIONS.forEach((direction) => {
      const tradeData = direction === BUY ? tradeDataBuy : tradeDataSell;
      if (tradeData === null || tradeData.length === 0) {
        return;
      }

      tradeData.forEach((series) => {
        const {
          key, label, subLabel, data: seriesData, meter, property,
        } = series;
        if (Object.entries(seriesData).length === 0) {
          return;
        }
        const seriesDataWithoutNaN = Object.entries(seriesData)
          .filter(([_, { [unitKey]: value }]) => !Number.isNaN(value));

        seriesDataWithoutNaN.forEach(([_, d]) => {
          const { interval, [unitKey]: value } = d;
          const { timestamp } = interval;

          allXAxisValues[timestamp] = true;

          if (!(key in processedData[direction])) { processedData[direction][key] = {}; }
          const dataKey = processedData[direction][key];

          if (!(timestamp in dataKey)) { dataKey[timestamp] = { x: timestamp, y: 0 }; }
          const dataKeyTS = dataKey[timestamp];

          dataKeyTS.y += value;

          if (direction === SELL) {
            if (!(timestamp in offset)) {
              offset[timestamp] = {
                x: timestamp, y: 0, y0: 0,
              };
            }
            offset[timestamp].y -= value;
            offset[timestamp].y0 -= value;
          }

          if (tooltipTimestamp && timestamp.equals(tooltipTimestamp)) {
            if (!(key in processedTooltipData[direction])) {
              processedTooltipData[direction][key] = {
                key, label, subLabel, value: 0, meter, property,
              };
            }
            processedTooltipData[direction][key].value += value;
          }
        });
      });
    });

    // Fill in missing values
    Object.keys(allXAxisValues).forEach((x) => {
      Object.keys(processedData).forEach((direction) => {
        Object.keys(processedData[direction]).forEach((key) => {
          if (!(x in processedData[direction][key])) {
            processedData[direction][key][x] = { x: DateTime.fromISO(x, { zone: timezone }), y: 0 };
          }
        });
      });
      if (!(x in offset)) {
        offset[x] = { x: DateTime.fromISO(x, { zone: timezone }), y: 0, y0: 0 };
      }
    });

    processedTooltipData.all = Object.values(processedData).map(
      (dataByKey) => Object.values(dataByKey).map(
        (dataByTimestamp) => dataByTimestamp[tooltipTimestamp],
      ),
    ).flat();

    // dont show untraded energy if the value is 0
    processedTooltipData.buy = Object.values(processedTooltipData.buy)
      .filter((tradeType) => !(tradeType.value === 0
        && tradeType.key.includes(UNTRADED_ENERGY_KEY)));
    processedTooltipData.sell = Object.values(processedTooltipData.sell)
      .filter((tradeType) => !(tradeType.value === 0
        && tradeType.key.includes(UNTRADED_ENERGY_KEY)));

    return { data: processedData, tooltipData: processedTooltipData };
  }, [tradeDataBuy, tradeDataSell, unitKey, tooltipTimestamp, timezone]);

  const toolTipProps = {
    isCarbon: unit === UNIT_CARBON,
    multipleMeter,
    tooltipDateFormat,
    tooltipData,
    tooltipFormat,
    tooltipLabelFunc,
    tooltipTimestamp,
    unit,
    unitLabel,
  };

  const stackBarProps = {
    data,
    hoverKeys,
    selectedKeys,
    compareByXFunc: compareByX,
    tooltipUpdate,
    handleChartClick,
    chartType: TRADE,
  };

  return (
    <StackedBarChart
      chartView={chartView}
      stackBarProps={stackBarProps}
      stepSize={aggregation}
      timespan={timespan}
      timezone={timezone}
      toolTipProps={toolTipProps}
      width={width}
      yAxisFormat={yAxisFormat}
    />
  );
}

const tradeDataShape = PropTypes.shape({
  aggregation: PropTypes.string,
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  data: PropTypes.objectOf(PropTypes.shape({
    interval: PropTypes.shape({
      timestamp: PropTypes.instanceOf(DateTime).isRequired,
    }).isRequired,
    volume: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  })),
});

ChartTrade.propTypes = {
  aggregation: PropTypes.string.isRequired,
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
  }).isRequired,
  handleChartClick: PropTypes.func.isRequired,
  hoverKeys: PropTypes.arrayOf(PropTypes.string),
  multipleMeter: PropTypes.bool,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  tradeDataBuy: PropTypes.arrayOf(tradeDataShape),
  tradeDataSell: PropTypes.arrayOf(tradeDataShape),
  tooltipLabelFunc: PropTypes.func.isRequired,
  tooltipTimestamp: PropTypes.instanceOf(DateTime),
  tooltipUpdate: PropTypes.func.isRequired,
  unit: PropTypes.oneOf([
    UNIT_CURRENCY,
    UNIT_ENERGY,
    UNIT_CARBON,
  ]).isRequired,
  width: PropTypes.number.isRequired,
};

export default React.memo(ChartTrade);
